import { useId, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Check, Circle, Clipboard, RefreshCw } from "lucide-react";
import {
  auditCriteria,
  qualificationKeys,
  qualificationLabels,
  type Qualification,
  type QualificationKey,
} from "@/shared/qualification";
import { api, ApiError, errorMessage } from "@/web/lib/api";
import { Badge, Button } from "./ui";

const questions: Record<QualificationKey, string> = {
  need: "Quel problème souhaitez-vous résoudre sur vos factures de transport ?",
  decisionMaker: "Qui participe à la décision de lancer un audit ?",
  deadline: "À quelle échéance souhaitez-vous lancer ce premier audit ?",
  documents: "Disposez-vous des factures de transport et documents nécessaires à un premier audit ?",
  budget: "Quel est votre budget de transport, et pour quelle période ?",
  carriers: "Avec quels transporteurs travaillez-vous ?",
  volumes: "Combien d’expéditions réalisez-vous, et sur quelle période ?",
  controls: "Comment contrôlez-vous actuellement vos factures de transport ?",
};
const stages = {
  incomplete: "À compléter",
  review: "À valider par l’équipe",
  ready: "Prêt pour un premier audit",
  out_of_scope: "Hors cible",
};
const date = (value: string) => new Date(value).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

function followUp(data: Qualification, name: string) {
  const missing = data.missing;
  return `Bonjour ${name},\n\n${
    missing.length
      ? `Pour préparer un premier audit de vos factures de transport, pourriez-vous préciser les points suivants ?\n\n${missing.map((key) => `• ${questions[key]}`).join("\n")}`
      : "Merci pour ces informations. Seriez-vous disponible pour convenir des prochaines étapes d’un premier audit de vos factures de transport ?"
  }\n\nMerci,\nL’équipe Fretguard`;
}

export function QualificationPanel({ customerId, customerName }: { customerId: string; customerName: string }) {
  const id = useId();
  const queryClient = useQueryClient();
  const path = `/customers/${customerId}/qualification`;
  const key = ["qualification", customerId];
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => api<Qualification>(path, { signal }),
    refetchInterval: (query) =>
      query.state.data?.enabled && query.state.data.available && query.state.data.status !== "idle" ? 10_000 : false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState<{ key: QualificationKey; value: string; version: number } | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [excluding, setExcluding] = useState(false);
  const [reason, setReason] = useState("");

  async function update(body: Record<string, unknown>, message: string, analyze = false) {
    if (!query.data) return false;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const updated = await api<Qualification>(analyze ? `${path}/analyze` : path, {
        method: analyze ? "POST" : "PATCH",
        body: JSON.stringify(analyze ? {} : { version: query.data.version, ...body }),
      });
      queryClient.setQueryData(key, updated);
      setNotice(message);
      return true;
    } catch (error) {
      setError(errorMessage(error, "La qualification n’a pas pu être enregistrée. Réessayez."));
      if (error instanceof ApiError && error.status === 409) await query.refetch();
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function saveField(event: FormEvent) {
    event.preventDefault();
    if (
      editing &&
      (await update(
        { version: editing.version, fields: { [editing.key]: editing.value.trim() || null } },
        "Correction enregistrée. Elle sera conservée lors des prochaines analyses.",
      ))
    )
      setEditing(null);
  }
  async function copyDraft() {
    try {
      await navigator.clipboard.writeText(draft ?? "");
      setNotice("Brouillon copié. Relisez-le avant de l’envoyer.");
    } catch {
      setError("La copie est indisponible. Sélectionnez et copiez le texte du brouillon.");
    }
  }
  if (query.isPending)
    return (
      <section className="qualification" aria-busy="true">
        <h3>Qualification du prospect</h3>
        <p role="status">Chargement de la fiche…</p>
      </section>
    );
  if (!query.data)
    return (
      <section className="qualification">
        <h3>Qualification du prospect</h3>
        <p role="alert">{errorMessage(query.error, "La fiche est indisponible.")}</p>
        <Button size="small" variant="secondary" onClick={() => void query.refetch()}>
          Réessayer
        </Button>
      </section>
    );
  const data = query.data;
  const conflicts = qualificationKeys.filter((key) => data.fields[key].suggestion);
  const secondaryConflict = conflicts.some((key) => !auditCriteria.includes(key));
  const canApprove =
    !data.missing.length &&
    !conflicts.length &&
    (data.status === "idle" || auditCriteria.every((key) => data.fields[key].manual));

  function renderField(fieldKey: QualificationKey) {
    const field = data.fields[fieldKey];
    const editor = editing?.key === fieldKey ? editing : null;
    const label = qualificationLabels[fieldKey];
    return (
      <div className="qualification-field" key={fieldKey}>
        <div className="qualification-field-heading">
          <h4>{label}</h4>
          <Button
            type="button"
            size="small"
            variant="ghost"
            disabled={busy || Boolean(editing)}
            aria-label={`Modifier ${label}`}
            onClick={() => {
              setEditing({ key: fieldKey, value: field.value ?? "", version: data.version });
              setError("");
              setNotice("");
            }}
          >
            Modifier
          </Button>
        </div>
        {editor ? (
          <form onSubmit={saveField} className="qualification-editor">
            <label htmlFor={`${id}-${fieldKey}`}>{label}</label>
            <textarea
              id={`${id}-${fieldKey}`}
              value={editor.value}
              maxLength={2000}
              rows={3}
              onChange={(event) => setEditing({ ...editor, value: event.target.value })}
            />
            <p className="qualification-meta">
              Laissez vide pour confirmer que cette information reste inconnue. La correction est protégée des analyses
              automatiques.
            </p>
            {editor.version !== data.version && (
              <div className="qualification-warning">
                <p>
                  La fiche a changé. Valeur actuelle : {field.value ?? "inconnue"}. Votre texte est conservé ci-dessus.
                </p>
                <Button
                  type="button"
                  size="small"
                  variant="secondary"
                  onClick={() => setEditing({ ...editor, version: data.version })}
                >
                  Reprendre sur la fiche actualisée
                </Button>
              </div>
            )}
            <div className="qualification-actions">
              <Button size="small" type="submit" disabled={busy || editor.version !== data.version}>
                Enregistrer la correction
              </Button>
              <Button size="small" type="button" variant="ghost" disabled={busy} onClick={() => setEditing(null)}>
                Annuler
              </Button>
            </div>
          </form>
        ) : (
          <>
            <p className={field.value ? "qualification-value" : "qualification-unknown"}>
              {field.value ?? "À renseigner"}
            </p>
            {field.manual && <p className="qualification-meta">Correction de l’équipe · protégée</p>}
            {field.source && (
              <details className="qualification-source">
                <summary>
                  {field.manual ? "Extrait analysé" : "Source"} ·{" "}
                  {field.source.kind === "notes" ? "Notes du contact" : "Conversation"} · {date(field.source.date)}
                </summary>
                <blockquote>{field.source.quote}</blockquote>
                {field.source.ticketId && <Link to={`/inbox/${field.source.ticketId}`}>Ouvrir la conversation</Link>}
              </details>
            )}
            {field.suggestion && (
              <div className="qualification-warning">
                <strong>Information différente à vérifier</strong>
                <blockquote>{field.suggestion.value}</blockquote>
                <p className="qualification-meta">
                  {field.suggestion.source.kind === "notes" ? "Notes du contact" : "Conversation"} ·{" "}
                  {date(field.suggestion.source.date)}
                </p>
                <div className="qualification-actions">
                  <Button
                    size="small"
                    type="button"
                    variant="secondary"
                    disabled={busy || Boolean(editing)}
                    onClick={() =>
                      void update(
                        { fields: { [fieldKey]: field.suggestion!.value } },
                        "Nouvelle information confirmée par l’équipe.",
                      )
                    }
                  >
                    Retenir cette information
                  </Button>
                  <Button
                    size="small"
                    type="button"
                    variant="ghost"
                    disabled={busy || Boolean(editing)}
                    onClick={() =>
                      void update({ fields: { [fieldKey]: field.value } }, "Valeur actuelle confirmée par l’équipe.")
                    }
                  >
                    Conserver la valeur actuelle
                  </Button>
                </div>
              </div>
            )}
            {field.manual && (
              <Button
                type="button"
                size="small"
                variant="ghost"
                disabled={busy || Boolean(editing)}
                onClick={() =>
                  void update({ release: [fieldKey] }, "Le champ utilise de nouveau les sources de l’analyse.")
                }
              >
                Utiliser à nouveau l’analyse
              </Button>
            )}
          </>
        )}
      </div>
    );
  }
  return (
    <section className="qualification" aria-labelledby={`${id}-title`}>
      <header className="qualification-heading">
        <h3 id={`${id}-title`}>Qualification du prospect</h3>
        <Badge tone={data.stage === "ready" ? "green" : data.stage === "out_of_scope" ? "neutral" : "amber"}>
          {stages[data.stage]}
        </Badge>
      </header>
      <p className="qualification-progress">{4 - data.missing.length} / 4 critères documentés</p>
      <ul className="qualification-criteria" aria-label="Critères pour un premier audit">
        {auditCriteria.map((fieldKey) => (
          <li key={fieldKey}>
            {data.fields[fieldKey].value ? (
              <Check size={15} aria-hidden="true" />
            ) : (
              <Circle size={15} aria-hidden="true" />
            )}
            <span>
              {qualificationLabels[fieldKey]}
              <small>{data.fields[fieldKey].value ? "Documenté" : "À compléter"}</small>
            </span>
          </li>
        ))}
      </ul>
      <div className="qualification-next">
        <strong>Prochaine action</strong>
        <p>
          {data.stage === "out_of_scope"
            ? data.excludedReason
            : conflicts.length
              ? "Vérifier les informations différentes avec l’équipe ou le prospect."
              : data.missing.length
                ? questions[data.missing[0]]
                : data.stage === "ready"
                  ? "Proposer au prospect les prochaines étapes du premier audit."
                  : "Relire les sources et faire valider la préparation par l’équipe."}
        </p>
      </div>
      {!data.enabled || !data.available ? (
        <p className="qualification-meta">
          L’analyse automatique est {data.enabled ? "indisponible" : "désactivée"}. Vous pouvez compléter la fiche
          manuellement. <Link to="/settings#qualification">Réglages IA</Link>
        </p>
      ) : (
        <div className="qualification-analysis">
          <p className="qualification-meta" role="status">
            {data.status === "quota"
              ? `Quota gratuit atteint. Reprise après ${date(new Date(data.nextAttemptAt).toISOString())}.`
              : data.status === "error"
                ? "L’analyse a échoué. Nouvelle tentative automatique dans quelques minutes."
                : data.status === "running"
                  ? "Analyse des sources en cours…"
                  : data.status === "pending"
                    ? "De nouvelles informations attendent une analyse automatique."
                    : data.analyzedAt
                      ? `Dernière analyse : ${date(data.analyzedAt)}`
                      : "Aucune source exploitable pour le moment."}
          </p>
          <Button
            type="button"
            variant="secondary"
            size="small"
            disabled={busy || Boolean(editing) || data.status === "running" || data.nextAttemptAt > query.dataUpdatedAt}
            onClick={() => void update({}, "", true)}
          >
            <RefreshCw size={14} aria-hidden="true" />
            {busy ? "Traitement…" : "Actualiser l’analyse"}
          </Button>
        </div>
      )}
      {data.partial && (
        <p className="qualification-warning">
          Historique partiellement analysé : jusqu’à 48 messages récents et 32 passages courts. Vérifiez aussi les
          échanges plus anciens et les pièces jointes.
        </p>
      )}
      {auditCriteria.map(renderField)}
      <details className="qualification-secondary" open={secondaryConflict}>
        <summary>Budget, transporteurs et fonctionnement{secondaryConflict ? " · Écart à vérifier" : ""}</summary>
        {qualificationKeys.filter((key) => !auditCriteria.includes(key)).map(renderField)}
      </details>
      <div className="qualification-review">
        {data.stage === "ready" || data.stage === "out_of_scope" ? (
          <>
            <p className="qualification-meta">Validé par l’équipe le {date(data.approvedAt!)}.</p>
            <Button
              type="button"
              variant="secondary"
              size="small"
              disabled={busy || Boolean(editing)}
              onClick={() => void update({ review: "reset" }, "Validation retirée. La fiche peut être réexaminée.")}
            >
              Réexaminer la qualification
            </Button>
          </>
        ) : (
          <>
            <p className="qualification-meta">
              Le premier audit exige ces quatre critères et une validation de l’équipe.
            </p>
            <Button
              type="button"
              size="small"
              disabled={!canApprove || busy || Boolean(editing)}
              onClick={() => void update({ review: "ready" }, "Préparation du premier audit validée par l’équipe.")}
            >
              Valider pour un premier audit
            </Button>
            {!canApprove && (
              <p className="qualification-meta">
                {conflicts.length
                  ? "Résolvez les écarts signalés avant de valider."
                  : data.missing.length
                    ? "Complétez les critères manquants avant de valider."
                    : "Attendez l’analyse ou confirmez les quatre critères manuellement."}
              </p>
            )}
            <Button
              type="button"
              size="small"
              variant="ghost"
              disabled={busy || Boolean(editing)}
              aria-expanded={excluding}
              onClick={() => setExcluding(!excluding)}
            >
              Déclarer hors cible
            </Button>
            {excluding && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void update({ review: "out_of_scope", reason }, "Prospect déclaré hors cible par l’équipe.").then(
                    (saved) => {
                      if (saved) setExcluding(false);
                    },
                  );
                }}
              >
                <label htmlFor={`${id}-reason`}>Motif de hors cible</label>
                <textarea
                  id={`${id}-reason`}
                  required
                  minLength={3}
                  maxLength={1000}
                  rows={2}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
                <Button type="submit" size="small" variant="secondary" disabled={busy}>
                  Confirmer hors cible
                </Button>
              </form>
            )}
          </>
        )}
      </div>
      {data.stage !== "out_of_scope" && (
        <div className="qualification-draft">
          <Button
            type="button"
            size="small"
            variant="secondary"
            aria-expanded={draft !== null}
            onClick={() => setDraft(draft === null ? followUp(data, customerName) : null)}
          >
            Préparer une relance
          </Button>
          {draft !== null && (
            <>
              <label htmlFor={`${id}-draft`}>Brouillon de relance</label>
              <textarea id={`${id}-draft`} rows={8} value={draft} onChange={(event) => setDraft(event.target.value)} />
              <p className="qualification-meta">À relire et adapter. Aucun message n’est envoyé automatiquement.</p>
              <Button type="button" variant="secondary" size="small" onClick={() => void copyDraft()}>
                <Clipboard size={14} aria-hidden="true" />
                Copier le brouillon
              </Button>
            </>
          )}
        </div>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <p className="qualification-notice" role="status">
        {notice}
      </p>
    </section>
  );
}
